import React from 'react';
import ReactSelect, {
  components,
  OptionProps,
  SingleValueProps,
} from 'react-select';
import Lang from '@lang/en';
import {
  BoldSpan,
  Container,
  customStyles,
  ErrorLabel,
  Icon,
  Label,
  theme,
  ThinSpan,
} from './styled';
import { UseFormRegisterReturn } from 'react-hook-form';
import { ActionMeta, ValueType } from 'react-select';
import { SelectOption } from '@app/common/types';

interface DropDownProps {
  options: SelectOption[];
  label?: string;
  error?: boolean;
  icon?: React.ReactElement;
  value?: SelectOption;
  beforeText?: string;
  placeholder?: string;
  registered?: UseFormRegisterReturn;
  isDisabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  className?: string;
  fixedWidth?: number;
  'data-testid'?: string;
  onChange?: (
    value: ValueType<SelectOption, false>,
    actionMeta: ActionMeta<SelectOption>
  ) => void;
}

interface CustomSelectValueProps extends SingleValueProps<SelectOption> {
  data: SelectOption;
  icon?: React.ReactElement;
  beforeText?: string;
}

const { Option } = components;
const CustomSelectOption = (
  props: OptionProps<SelectOption, false>
): React.ReactElement => {
  return <Option {...props}>{props.data.label}</Option>;
};

const CustomSelectValue = (
  props: CustomSelectValueProps
): React.ReactElement => {
  return (
    <Icon>
      {props.icon}
      <span>
        {props.beforeText ? (
          <>
            <ThinSpan>{props.beforeText}: </ThinSpan>
            <BoldSpan>{props.data.label}</BoldSpan>
          </>
        ) : (
          <ThinSpan>{props.data.label}</ThinSpan>
        )}
      </span>
    </Icon>
  );
};

const DropDown = ({
  label,
  value,
  options,
  isSearchable,
  isDisabled,
  isClearable,
  icon,
  beforeText,
  error,
  placeholder,
  registered,
  onChange,
  fixedWidth,
  className,
  ...props
}: DropDownProps): React.ReactElement => {
  return (
    <Container
      fixedWidth={fixedWidth}
      className={className}
      data-testid={props['data-testid']}
      isDisabled={isDisabled}
    >
      {label && (
        <Label isDisabled={isDisabled} error={error}>
          {label}
        </Label>
      )}
      <ReactSelect
        {...registered}
        options={options}
        isClearable={isClearable}
        isDisabled={isDisabled}
        isSearchable={isSearchable}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        components={{
          Option: CustomSelectOption,
          SingleValue: (data) =>
            CustomSelectValue({ ...data, icon, beforeText }),
        }}
        styles={customStyles}
        theme={theme}
        error={error}
      />
      {error && <ErrorLabel error>{Lang.bottomInputLabel}</ErrorLabel>}
    </Container>
  );
};

export default DropDown;
